import * as React from "react";
import { Check, ChevronDown, Search } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { Popover, PopoverContent, PopoverTrigger } from "./popover.js";
import { selectItemClasses, selectPanelClasses, selectTriggerClasses } from "./select.js";

// A Select with a search box at the top of its list, for long option lists such
// as employees or catalog skills. Looks identical to ui/select (it reuses the same
// style tokens); use a plain Select for short fixed lists.

export interface ComboboxOption {
  value: string;
  label: string;
  /** Secondary text shown under the label, and also matched by the search. */
  sub?: string | undefined;
}

export interface ComboboxProps {
  value: string;
  onValueChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string | undefined;
  searchPlaceholder?: string | undefined;
  emptyText?: string | undefined;
  disabled?: boolean | undefined;
  className?: string | undefined;
  id?: string | undefined;
}

function Combobox({
  value,
  onValueChange,
  options,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No matches.",
  disabled,
  className,
  id,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [active, setActive] = React.useState(0);
  const listRef = React.useRef<HTMLDivElement>(null);
  const listId = React.useId();

  const selected = options.find((o) => o.value === value);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.sub ?? "").toLowerCase().includes(q)
    );
  }, [options, query]);

  // Each time the list opens or the search changes, start on the selected option
  // if it is still visible, otherwise on the first match.
  React.useEffect(() => {
    if (!open) return;
    const idx = filtered.findIndex((o) => o.value === value);
    setActive(idx >= 0 ? idx : 0);
  }, [open, filtered, value]);

  React.useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  function choose(option: ComboboxOption) {
    onValueChange(option.value);
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const option = filtered[active];
      if (option) choose(option);
    }
  }

  return (
    // `modal` so the list stays wheel-scrollable when the combobox sits inside a
    // modal Dialog, which otherwise blocks scrolling outside its own content.
    <Popover
      modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger asChild disabled={disabled}>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          className={cn(selectTriggerClasses, "text-left", className)}
        >
          <span className={cn("truncate", !selected && "text-surface-400")}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-surface-400" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className={cn(selectPanelClasses, "w-[var(--radix-popover-trigger-width)] min-w-[12rem] p-0")}
      >
        <div className="flex items-center gap-2 border-b border-surface-100 px-3">
          <Search className="h-4 w-4 shrink-0 text-surface-400" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={searchPlaceholder}
            aria-controls={listId}
            aria-activedescendant={filtered[active] ? `${listId}-${active}` : undefined}
            className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-surface-400"
          />
        </div>

        <div ref={listRef} id={listId} role="listbox" className="max-h-64 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-[13px] text-surface-400">{emptyText}</p>
          ) : (
            filtered.map((option, i) => {
              const isSelected = option.value === value;
              return (
                <div
                  key={option.value}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={isSelected}
                  data-index={i}
                  onMouseEnter={() => setActive(i)}
                  // mousedown, not click, so the search input never loses focus
                  // before the choice registers.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(option);
                  }}
                  className={cn(selectItemClasses, i === active && "bg-brand-50 text-brand-700")}
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    {isSelected && <Check className="h-4 w-4" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate">{option.label}</span>
                    {option.sub && (
                      <span className="block truncate text-[11px] text-surface-400">{option.sub}</span>
                    )}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { Combobox };
